import type React from "react";
import { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  BookOpen,
  Video,
  ClipboardCheck,
  Clock,
  Trash2,
  Edit,
  CalendarClock,
  CalendarX,
} from "lucide-react";
import { ApiService } from "../../services/api";
import checkPermission from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import Modal from "../../components/ui/Modal";
import CalendarHeader from "./components/CalendarHeader";
import SelectedDaySidebar from "./components/SelectedDaySidebar";

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  isAllDay: boolean;
  isSystem?: boolean;
  // Sunucu söyler: kendi etkinliğin ya da (yetkiliysen) şube etkinliği. Yoksa eski kural: sistem etkinliği düzenlenmez.
  editable?: boolean;
  type?: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  referenceType?: "HOMEWORK" | "SURVEY" | "MEET" | string;
  status?: string;
}

// Koçum planının kalemi: öğrenci için ödevdir (Ödev Takvimi'nde); durum plan kaleminden gelir
const COACH_STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  COMPLETED: { text: "Yapıldı", cls: "bg-green-50 text-green-700 border-green-200" },
  FAILED: { text: "Kaçırıldı", cls: "bg-red-50 text-red-700 border-red-200" },
  SKIPPED: { text: "Atlandı", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  PENDING: { text: "Bekliyor", cls: "bg-amber-50 text-amber-800 border-amber-200" },
};
const isCoachTask = (event?: CalendarEvent | null) => Boolean(event?.metadata?.coach);
const CoachBadge = ({ event }: { event: CalendarEvent }) => {
  const st = COACH_STATUS_LABEL[event.status || "PENDING"] || COACH_STATUS_LABEL.PENDING;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">Koç ödevi</span>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${st.cls}`}>{st.text}</span>
    </span>
  );
};

interface CalendarDay {
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: CalendarEvent[];
  hasEvents: boolean;
  eventType?: "start" | "middle" | "end" | "single";
}

interface CalendarViewProps {
  onEventClick?: (event: CalendarEvent) => void;
  onEditEvent?: (event: CalendarEvent) => void;
  onDeleteEvent?: (event: CalendarEvent) => void;
  onCreateEvent?: (initialDate?: string) => void;
  onRefresh?: () => void;
  filterReferenceType?: "ALL" | "MEET" | "HOMEWORK" | "SURVEY" | "CUSTOM" | "APPOINTMENT";
  showCreateButton?: boolean;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  onEventClick,
  onEditEvent,
  onDeleteEvent,
  onCreateEvent,
  onRefresh,
  filterReferenceType,
  showCreateButton = false,
} = {}) => {
  const { showAlert } = useModal();
  const canListCalendars = checkPermission("calendar_list");
  const canAddCalendar = checkPermission("calendar_add");
  const canUpdateCalendar = checkPermission("calendar_update");
  const canDeleteCalendar = checkPermission("calendar_delete");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showMobileEventsModal, setShowMobileEventsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const toLocalDateString = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState<string>(toLocalDateString(new Date()));
  const [referenceType, setReferenceType] = useState<"ALL" | "MEET" | "HOMEWORK" | "SURVEY" | "CUSTOM" | "APPOINTMENT">(
    filterReferenceType ?? "ALL"
  );

  useEffect(() => {
    if (filterReferenceType && filterReferenceType !== referenceType) setReferenceType(filterReferenceType);
  }, [filterReferenceType, referenceType]);

  const daysOfWeek = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
  const daysOfWeekShort = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

  const getVisibleMonthRange = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
    const startLocal = toLocalDateString(start) + "T00:00:00";
    const endLocal = toLocalDateString(end) + "T23:59:59";
    return { startISO: startLocal, endISO: endLocal };
  };

  useEffect(() => {
    fetchEvents();
  }, [currentDate, referenceType]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) setSidebarOpen(false);
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const effectiveRef = referenceType;
      // limit: backend varsayılanı 100 — koç planı gibi yoğun aylarda (200+ etkinlik)
      // ay ortasından sonrası kesiliyordu ("19 Ağustos'tan sonrası görünmüyor").
      const filters: any = { year, month, limit: 1000 };

      let types: string[] = [];
      if (effectiveRef === "ALL") {
        types = [];
      } else if (effectiveRef === "CUSTOM") {
        types = ["USER", "DEPARTMENT"];
      } else {
        types = [effectiveRef];
      }

      if (types.length > 0) {
        filters.types = types.join(",");
      }

      const calendarsPromise = canListCalendars ? ApiService.getAllCalendars(filters) : Promise.resolve({ items: [] });
      const [calendarsResp] = await Promise.all([calendarsPromise]);
      let calendarEvents: CalendarEvent[] = (calendarsResp?.items || []) as CalendarEvent[];

      if (effectiveRef === "CUSTOM") {
        calendarEvents = calendarEvents.filter((event) => {
          const eventType = (event as any).type;
          const refType = event.metadata?.referenceType || event.referenceType;
          return (
            (eventType === "USER" || eventType === "DEPARTMENT") &&
            (!refType || !["HOMEWORK", "SURVEY", "MEET", "APPOINTMENT"].includes(refType))
          );
        });
      }

      setEvents(calendarEvents);
    } catch (err: any) {
      console.error("Calendar fetch error:", err);
      if (err?.message && String(err.message).includes("<!DOCTYPE")) {
        await showAlert({
          title: "Oturum Hatası",
          message: "Oturum süresi dolmuş. Lütfen tekrar giriş yapın.",
          type: "error",
        });
      } else {
        await showAlert({
          title: "Hata",
          message: "Etkinlikler yüklenemedi.",
          type: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  const todayString = toLocalDateString(today);

  const isDateInEventRange = (date: string, startDate: string, endDate: string): boolean => {
    const startLocal = toLocalDateString(new Date(startDate));
    const endLocal = toLocalDateString(new Date(endDate));
    return date >= startLocal && date <= endLocal;
  };

  const getEventTypeForDate = (date: string, startDate: string, endDate: string): "start" | "middle" | "end" | "single" => {
    const startDateOnly = startDate.split("T")[0];
    const endDateOnly = endDate.split("T")[0];
    if (startDateOnly === endDateOnly) return "single";
    if (date === startDateOnly) return "start";
    if (date === endDateOnly) return "end";
    return "middle";
  };

  const getDaysInMonth = (date: Date): CalendarDay[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    let startDay = firstDay.getDay();
    startDay = startDay === 0 ? 7 : startDay;
    const days: CalendarDay[] = [];

    for (let i = startDay - 2; i >= 0; i--) {
      days.push({
        day: 0,
        isCurrentMonth: false,
        isToday: false,
        events: [],
        hasEvents: false,
        eventType: undefined,
      });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const dayEvents = events.filter((event) => isDateInEventRange(dateString, event.startDate, event.endDate));
      let hasEvents = false;
      let eventType: "start" | "middle" | "end" | "single" | undefined;
      for (const event of events) {
        if (isDateInEventRange(dateString, event.startDate, event.endDate)) {
          hasEvents = true;
          eventType = getEventTypeForDate(dateString, event.startDate, event.endDate);
          break;
        }
      }
      days.push({
        day,
        isCurrentMonth: true,
        isToday: dateString === todayString,
        events: dayEvents,
        hasEvents,
        eventType,
      });
    }
    const remainingDays = 42 - days.length;
    for (let i = 0; i < remainingDays; i++)
      days.push({
        day: 0,
        isCurrentMonth: false,
        isToday: false,
        events: [],
        hasEvents: false,
        eventType: undefined,
      });
    return days;
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === "prev") newDate.setMonth(prev.getMonth() - 1);
      else newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  const toggleSidebar = () => {
    if (window.innerWidth < 768) setShowMobileEventsModal(true);
    else setSidebarOpen(!sidebarOpen);
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowEventModal(true);
    onEventClick?.(event);
  };

  const formatEventTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  const formatEventDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  const getSelectedDateEvents = () => events.filter((event) => isDateInEventRange(selectedDate, event.startDate, event.endDate));

  const getEventColor = (eventId: string) => {
    const colors = [
      "bg-gradient-to-br from-rose-100 to-pink-100 text-rose-700 border-rose-200",
      "bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700 border-blue-200",
      "bg-gradient-to-br from-green-100 to-emerald-100 text-green-700 border-green-200",
      "bg-gradient-to-br from-amber-100 to-yellow-100 text-amber-700 border-amber-200",
      "bg-gradient-to-br from-purple-100 to-violet-100 text-purple-700 border-purple-200",
      "bg-gradient-to-br from-pink-100 to-fuchsia-100 text-pink-700 border-pink-200",
      "bg-gradient-to-br from-indigo-100 to-blue-100 text-indigo-700 border-indigo-200",
      "bg-gradient-to-br from-orange-100 to-amber-100 text-orange-700 border-orange-200",
      "bg-gradient-to-br from-cyan-100 to-sky-100 text-cyan-700 border-cyan-200",
      "bg-gradient-to-br from-lime-100 to-green-100 text-lime-700 border-lime-200",
      "bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-700 border-emerald-200",
      "bg-gradient-to-br from-violet-100 to-purple-100 text-violet-700 border-violet-200",
    ];
    let hash = 0;
    for (let i = 0; i < eventId.length; i++) {
      const char = eventId.charCodeAt(i);
      hash = (hash << 5) + hash + char;
      hash = hash & hash;
    }
    const salt = parseInt(eventId.replace(/[^0-9]/g, ""), 10) || 0;
    hash = Math.abs(hash + salt);
    return colors[hash % colors.length];
  };

  const getEventBackgroundColor = (eventId: string) => {
    const bgColors = [
      "bg-red-50",
      "bg-blue-50",
      "bg-green-50",
      "bg-yellow-50",
      "bg-purple-50",
      "bg-pink-50",
      "bg-indigo-50",
      "bg-orange-50",
      "bg-cyan-50",
      "bg-lime-50",
      "bg-emerald-50",
      "bg-violet-50",
    ];
    let hash = 0;
    for (let i = 0; i < eventId.length; i++) {
      const char = eventId.charCodeAt(i);
      hash = (hash << 5) + hash + char;
      hash = hash & hash;
    }
    const salt = parseInt(eventId.replace(/[^0-9]/g, ""), 10) || 0;
    hash = Math.abs(hash + salt);
    return bgColors[hash % bgColors.length];
  };

  const handleDayClick = (calDay: CalendarDay) => {
    let year = currentDate.getFullYear();
    let month = currentDate.getMonth();

    if (!calDay.isCurrentMonth) {
      if (calDay.day > 15) {
        month = month - 1;
        if (month < 0) {
          month = 11;
          year = year - 1;
        }
      } else {
        month = month + 1;
        if (month > 11) {
          month = 0;
          year = year + 1;
        }
      }
    }

    const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(calDay.day).padStart(2, "0")}`;
    setSelectedDate(dateString);

    if (window.innerWidth < 768) {
      setShowMobileEventsModal(true);
    } else {
      setSidebarOpen(true);
    }
  };

  const calendarDays = getDaysInMonth(currentDate);
  const weeks = [] as CalendarDay[][];
  for (let i = 0; i < calendarDays.length; i += 7) weeks.push(calendarDays.slice(i, i + 7));

  return (
    <div className="h-full bg-gradient-to-br from-gray-50 to-white overflow-hidden flex flex-col">
      <CalendarHeader
        currentDate={currentDate}
        referenceType={referenceType}
        filterReferenceType={filterReferenceType}
        onChangeReference={setReferenceType}
        onNavigate={navigateMonth}
        onToggleSidebar={toggleSidebar}
        sidebarOpen={sidebarOpen}
      />

      <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
        <div className="flex-1 bg-white flex flex-col overflow-hidden m-2 shadow-lg border border-gray-200">
          <div className="border-b-2 border-blue-100 flex-shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="grid grid-cols-7">
              {daysOfWeek.map((day, index) => (
                <div
                  key={index}
                  className="p-2 md:p-3 text-center text-xs md:text-sm font-semibold text-gray-700 bg-transparent border-r border-blue-100 last:border-r-0">
                  <span className="hidden md:inline">{day}</span>
                  <span className="md:hidden">{daysOfWeekShort[index]}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-auto md:overflow-visible bg-gray-50">
            <div className="grid grid-rows-6 h-full">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="border-b border-gray-100 flex-1 min-h-0">
                  <div className="grid grid-cols-7 h-full">
                    {week.map((calDay, dayIndex) => {
                      if (calDay.day === 0)
                        return (
                          <div
                            key={`${weekIndex}-${dayIndex}`}
                            className="h-full p-1 border-r border-gray-100 last:border-r-0 bg-gray-50/50"
                          />
                        );
                      return (
                        <div
                          key={`${weekIndex}-${dayIndex}`}
                          onClick={() => handleDayClick(calDay)}
                          className="h-full p-1 md:p-2 border-r border-gray-100 last:border-r-0 relative transition-all hover:shadow-md bg-white hover:bg-blue-50/30 cursor-pointer">
                          <div
                            className={`text-xs md:text-sm mb-1 transition-all ${(() => {
                              let year = currentDate.getFullYear();
                              let month = currentDate.getMonth();
                              const dayDateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(calDay.day).padStart(2, "0")}`;
                              const isSelected = dayDateString === selectedDate;
                              if (calDay.isToday)
                                return "bg-gradient-to-br from-red-500 to-orange-500 text-white w-7 h-7 rounded-full flex items-center justify-center font-bold hover:from-red-600 hover:to-orange-600 shadow-lg ring-2 ring-orange-300";
                              if (isSelected)
                                return "bg-blue-500 text-white w-7 h-7 rounded-full flex items-center justify-center font-semibold hover:bg-blue-600 shadow-md";
                              return "text-gray-700 hover:bg-blue-100 w-7 h-7 rounded-full flex items-center justify-center font-medium hover:shadow-sm";
                            })()}`}>
                            {calDay.day}
                          </div>

                          {calDay.events.length > 0 && (
                            <div className="flex justify-center items-center gap-1 mt-1">
                              {(() => {
                                const getEventType = (e: CalendarEvent) =>
                                  e.referenceType ||
                                  e.metadata?.referenceType ||
                                  e.metadata?.type ||
                                  (e as any).reference?.type ||
                                  (e as any).type ||
                                  null;
                                const totalCount = calDay.events.length;

                                if (referenceType === "HOMEWORK") {
                                  return (
                                    <div title={`${totalCount} Ödev`} className="relative flex items-center justify-center">
                                      <BookOpen className="w-5 h-5 text-orange-600 drop-shadow-md" strokeWidth={2.5} />
                                      <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                        {totalCount}
                                      </span>
                                    </div>
                                  );
                                }

                                if (referenceType === "SURVEY") {
                                  return (
                                    <div title={`${totalCount} Anket`} className="relative flex items-center justify-center">
                                      <ClipboardCheck className="w-5 h-5 text-red-600 drop-shadow-md" strokeWidth={2.5} />
                                      <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                        {totalCount}
                                      </span>
                                    </div>
                                  );
                                }

                                if (referenceType === "MEET") {
                                  return (
                                    <div title={`${totalCount} Toplantı`} className="relative flex items-center justify-center">
                                      <Video className="w-5 h-5 text-cyan-600 drop-shadow-md" strokeWidth={2.5} />
                                      <span className="absolute -top-2 -right-2 bg-cyan-600 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                        {totalCount}
                                      </span>
                                    </div>
                                  );
                                }

                                if (referenceType === "APPOINTMENT") {
                                  return (
                                    <div title={`${totalCount} Randevu`} className="relative flex items-center justify-center">
                                      <CalendarClock className="w-5 h-5 text-teal-600 drop-shadow-md" strokeWidth={2.5} />
                                      <span className="absolute -top-2 -right-2 bg-teal-600 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                        {totalCount}
                                      </span>
                                    </div>
                                  );
                                }

                                if (referenceType === "CUSTOM") {
                                  return (
                                    <div className="relative flex items-center justify-center" title={`${totalCount} Etkinlik`}>
                                      <CalendarIcon className="w-5 h-5 text-blue-700 drop-shadow-md" strokeWidth={2.5} />
                                      <span className="absolute -top-2 -right-2 bg-blue-700 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                        {totalCount}
                                      </span>
                                    </div>
                                  );
                                }

                                const homeworkCount = calDay.events.filter((e) => getEventType(e) === "HOMEWORK").length;
                                const surveyCount = calDay.events.filter((e) => getEventType(e) === "SURVEY").length;
                                const meetCount = calDay.events.filter((e) => getEventType(e) === "MEET").length;
                                const appointmentCount = calDay.events.filter((e) => getEventType(e) === "APPOINTMENT").length;
                                const otherCount = calDay.events.filter((e) => {
                                  const type = getEventType(e);
                                  return !type || !["HOMEWORK", "SURVEY", "MEET", "APPOINTMENT"].includes(type);
                                }).length;

                                return (
                                  <>
                                    {homeworkCount > 0 && (
                                      <div title={`${homeworkCount} Ödev`} className="relative flex items-center justify-center">
                                        <BookOpen className="w-5 h-5 text-orange-600 drop-shadow-md" strokeWidth={2.5} />
                                        <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                          {homeworkCount}
                                        </span>
                                      </div>
                                    )}
                                    {surveyCount > 0 && (
                                      <div title={`${surveyCount} Anket`} className="relative flex items-center justify-center">
                                        <ClipboardCheck className="w-5 h-5 text-red-600 drop-shadow-md" strokeWidth={2.5} />
                                        <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                          {surveyCount}
                                        </span>
                                      </div>
                                    )}
                                    {meetCount > 0 && (
                                      <div title={`${meetCount} Toplantı`} className="relative flex items-center justify-center">
                                        <Video className="w-5 h-5 text-cyan-600 drop-shadow-md" strokeWidth={2.5} />
                                        <span className="absolute -top-2 -right-2 bg-cyan-600 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                          {meetCount}
                                        </span>
                                      </div>
                                    )}
                                    {appointmentCount > 0 && (
                                      <div title={`${appointmentCount} Randevu`} className="relative flex items-center justify-center">
                                        <CalendarClock className="w-5 h-5 text-teal-600 drop-shadow-md" strokeWidth={2.5} />
                                        <span className="absolute -top-2 -right-2 bg-teal-600 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                          {appointmentCount}
                                        </span>
                                      </div>
                                    )}
                                    {otherCount > 0 &&
                                      homeworkCount === 0 &&
                                      surveyCount === 0 &&
                                      meetCount === 0 &&
                                      appointmentCount === 0 && (
                                        <div className="relative flex items-center justify-center" title={`${otherCount} Etkinlik`}>
                                          <CalendarIcon className="w-5 h-5 text-blue-700 drop-shadow-md" strokeWidth={2.5} />
                                          <span className="absolute -top-2 -right-2 bg-blue-700 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center shadow-lg border border-white">
                                            {otherCount}
                                          </span>
                                        </div>
                                      )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {sidebarOpen && (
          <SelectedDaySidebar
            title={formatEventDate(selectedDate)}
            events={getSelectedDateEvents().map((e) => ({
              id: e.id,
              title: e.title,
              description: isCoachTask(e)
                ? `Koç ödevi · ${(COACH_STATUS_LABEL[e.status || "PENDING"] || COACH_STATUS_LABEL.PENDING).text}${e.description ? ` — ${e.description}` : ""}`
                : e.description,
            }))}
            canCreate={!!(canAddCalendar && showCreateButton)}
            onCreate={() => onCreateEvent?.(selectedDate)}
            onEventClick={(e) => {
              const found = events.find((x) => x.id === e.id);
              if (found) handleEventClick(found);
            }}
            getEventColor={getEventColor}
          />
        )}
      </div>

      <Modal isOpen={showMobileEventsModal} onClose={() => setShowMobileEventsModal(false)} title={formatEventDate(selectedDate)} size="md">
        <div className="space-y-2">
          {getSelectedDateEvents().length > 0 ? (
            getSelectedDateEvents().map((event) => (
              <div
                key={event.id}
                onClick={() => {
                  handleEventClick(event);
                  setShowMobileEventsModal(false);
                }}
                className={`p-3 rounded-lg text-xs cursor-pointer transition-all hover:shadow-lg border ${getEventColor(event.id)}`}>
                <div className="font-semibold">{event.title}</div>
                {isCoachTask(event) && <div className="mt-1"><CoachBadge event={event} /></div>}
                {event.description && <div className="text-xs opacity-75 mt-1 line-clamp-2">{event.description}</div>}
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-8 text-gray-400">
              <CalendarX className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-sm">Bu günde etkinlik bulunmuyor.</p>
            </div>
          )}

          {canAddCalendar && showCreateButton && (
            <button
              onClick={() => {
                onCreateEvent?.(selectedDate);
                setShowMobileEventsModal(false);
              }}
              className="w-full bg-green-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-green-700 shadow-md hover:shadow-lg transition-all mt-3">
              + Yeni Etkinlik
            </button>
          )}
        </div>
      </Modal>

      <Modal isOpen={showEventModal && !!selectedEvent} onClose={() => setShowEventModal(false)} title={isCoachTask(selectedEvent) ? "Koç Ödevi" : "Etkinlik Detayları"} size="md">
        {selectedEvent && (
          <>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-xl text-gray-900 mb-3">{selectedEvent.title}</h4>
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedEvent.isAllDay ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                      <CalendarIcon className="w-3 h-3" />
                      Tüm Gün
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                      <Clock className="w-3 h-3" />
                      Saatli Etkinlik
                    </span>
                  )}
                  {isCoachTask(selectedEvent) ? (
                    <CoachBadge event={selectedEvent} />
                  ) : selectedEvent.isSystem && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                      Sistem Etkinliği
                    </span>
                  )}
                  {selectedEvent.type === "DEPARTMENT" && selectedEvent.editable && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
                      Şube etkinliği: değişiklik bütün şubelere uygulanır
                    </span>
                  )}
                </div>
              </div>

              {selectedEvent.description && (
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-sm text-gray-700 leading-relaxed">{selectedEvent.description}</p>
                </div>
              )}

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 p-1.5 bg-green-100 rounded-lg">
                    <CalendarIcon className="w-4 h-4 text-green-700" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-500 mb-0.5">Başlangıç</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatEventDate(selectedEvent.startDate)}
                      {!selectedEvent.isAllDay && (
                        <span className="ml-2 text-gray-600 font-normal">{formatEventTime(selectedEvent.startDate)}</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-0.5 p-1.5 bg-red-100 rounded-lg">
                    <CalendarIcon className="w-4 h-4 text-red-700" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-500 mb-0.5">Bitiş</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatEventDate(selectedEvent.endDate)}
                      {!selectedEvent.isAllDay && (
                        <span className="ml-2 text-gray-600 font-normal">{formatEventTime(selectedEvent.endDate)}</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-5 flex justify-between gap-2 border-t border-gray-200 mt-5">
              {canDeleteCalendar && (selectedEvent.editable ?? !selectedEvent.isSystem) && (filterReferenceType === "CUSTOM" || !filterReferenceType) ? (
                <button
                  onClick={() => {
                    if (onDeleteEvent && selectedEvent) {
                      onDeleteEvent(selectedEvent);
                      setShowEventModal(false);
                    }
                  }}
                  className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">
                  <Trash2 className="w-4 h-4" />
                  Sil
                </button>
              ) : (
                <div />
              )}
              <div className="flex gap-2">
                {canUpdateCalendar && (selectedEvent.editable ?? !selectedEvent.isSystem) && (filterReferenceType === "CUSTOM" || !filterReferenceType) && (
                  <button
                    onClick={async () => {
                      if (onEditEvent && selectedEvent) {
                        onEditEvent(selectedEvent);
                        setShowEventModal(false);
                      }
                    }}
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                    <Edit className="w-4 h-4" />
                    Düzenle
                  </button>
                )}
                <button
                  onClick={() => setShowEventModal(false)}
                  className="bg-white px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  Kapat
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CalendarView;
